import React, { useEffect, useState, useMemo } from 'react';
import { Typography, Tooltip } from '@mui/material';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import styles from './ModernGoalWidget.module.css';
import dashboardStyles from '../../DashboardPage.module.css';

interface ModernGoalWidgetProps {
    empresa?: string;
    currentSales: number;
    targetSales: number;
    currentMargin: number;
    targetMargin: number;
    loading?: boolean;
    scoreFinal?: number;
    diasRestantes?: number;
    nextMonthTarget?: number;
    nextMonthSales?: number;
    nextMonthScore?: number;
}

const formatCurrency = (v: number) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact' });
const formatCurrencyFull = (v: number) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ModernGoalWidget: React.FC<ModernGoalWidgetProps> = ({
    currentSales, targetSales, currentMargin, targetMargin, loading = false, scoreFinal, diasRestantes: diasRestantesProp, nextMonthTarget, nextMonthSales, nextMonthScore, empresa
}) => {
    const [score, setScore] = useState(0);
    const [simOpen, setSimOpen] = useState(false);
    const [simValor, setSimValor] = useState('');
    const [simMargem, setSimMargem] = useState('');

    useEffect(() => {
        if (scoreFinal !== undefined) { setScore(scoreFinal); return; }
        if (targetSales === 0) { setScore(0); return; }
        const av = Math.min(currentSales / targetSales, 2);
        let fs = av;
        if (targetMargin > 0) {
            fs = (av * 0.5) + (Math.min(currentMargin / targetMargin, 2) * 0.5);
        }
        setScore(fs * 100);
    }, [currentSales, targetSales, currentMargin, targetMargin, scoreFinal]);

    // Fecha modal ao pressionar Escape
    useEffect(() => {
        if (!simOpen) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setSimOpen(false);
                setSimValor('');
                setSimMargem('');
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [simOpen]);

    const hoje = useMemo(() => new Date(), []);
    const ultimoDiaMes = useMemo(() => new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0), [hoje]);
    const diasRestantesCalc = useMemo(() => Math.max(0, ultimoDiaMes.getDate() - hoje.getDate()), [hoje, ultimoDiaMes]);
    const diasRestantes = diasRestantesProp ?? diasRestantesCalc;
    const faltaMeta = targetSales - currentSales;
    const faltaMargem = targetMargin - currentMargin;
    const mediaDiaria = diasRestantes > 0 && faltaMeta > 0 ? faltaMeta / diasRestantes : 0;
    const progressPct = targetSales > 0 ? Math.min((currentSales / targetSales) * 100, 100) : 0;
    const marginProgressPct = targetMargin > 0 ? Math.min((currentMargin / targetMargin) * 100, 100) : 0;

    const primary = 'var(--theme-primary, #b91c1c)';
    const accent = 'var(--theme-accent, #1e293b)';
    const gaugePct = Math.min(score, 100);
    const displayPct = score >= 100 ? score.toFixed(1) + '%' : score.toFixed(2) + '%';
    const gaugeData = [
        { name: 'Atingido', value: gaugePct },
        { name: 'Restante', value: 100 - gaugePct },
    ];



    if (loading) return (
        <div className={styles.widgetContainer}>
            <div className={styles.skelHeader} />
            <div className={styles.skelMain}>
                <div className={styles.skelGaugeCol}>
                    <div className={styles.skelGauge} />
                </div>
                <div className={styles.skelIndCol}>
                    <div className={styles.skelInd} />
                    <div className={styles.skelInd} />
                    <div className={styles.skelInd} />
                    <div className={styles.skelInd} />
                    <div className={styles.skelInd} />
                </div>
            </div>
            <div className={styles.skelBottom}>
                <div className={styles.skelBar} />
                <div className={styles.skelBar} />
            </div>
        </div>
    );

    return (
        <div className={styles.widgetContainer}>
            <div className={styles.headerArea}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h2 className={dashboardStyles.widgetTitle} style={{ margin: 0 }}>Meta</h2>
                                    <button onClick={() => setSimOpen(true)}
                                        style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid var(--border-primary)', background: 'var(--bg-widget)', color: 'var(--text-secondary)', fontSize: 9, fontWeight: 700, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.03em', flexShrink: 0 }}>
                                        Simular
                                    </button>
                                </div>
            </div>
            <div className={styles.mainArea}>
                {/* Velocimetro */}
                <div className={styles.gaugeCol}>
                    <div className={styles.gaugeWrap}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={gaugeData} cx="50%" cy="100%" startAngle={180} endAngle={0} innerRadius={75} outerRadius={105} dataKey="value" stroke="none">
                                    <Cell fill={primary} />
                                    <Cell fill="var(--gauge-track)" />
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                        <div className={styles.gaugeValue}>
                            <Typography className={styles.scoreNum}>{displayPct}</Typography>
                        </div>
                    </div>
                    <div className={styles.gaugeFooter}>
                        <span className={styles.gaugeSales}>{formatCurrency(currentSales)}</span>
                        <span className={styles.gaugeTarget}>meta {formatCurrency(targetSales)}</span>
                    </div>
                </div>

                {/* Indicadores direita */}
                <div className={styles.indicatorsCol}>
                    <div className={styles.indicatorRow}>
                        <div className={styles.indHeader}>
                            <div className={styles.indIcon}><TrackChangesIcon sx={{ fontSize: 18, color: 'var(--text-secondary, #64748b)' }} /></div>
                            <span className={styles.indLabel}>VENDAS RESTANTES</span>
                        </div>
                        <div className={styles.indValuePrimary}>{faltaMeta > 0 ? formatCurrency(faltaMeta) : 'R$ 0'}</div>
                    </div>
                    {targetMargin > 0 && (
                        <div className={styles.indicatorRow}>
                            <div className={styles.indHeader}>
                                <div className={styles.indIcon}><TrackChangesIcon sx={{ fontSize: 18, color: 'var(--text-secondary, #64748b)' }} /></div>
                                <span className={styles.indLabel}>MARGEM RESTANTE</span>
                            </div>
                            <div className={styles.indValuePrimary}>{faltaMargem > 0 ? formatCurrency(faltaMargem) : 'R$ 0'}</div>
                        </div>
                    )}
                    <div className={styles.indicatorRow}>
                        <div className={styles.indHeader}>
                            <div className={styles.indIcon}><CalendarTodayIcon sx={{ fontSize: 18, color: 'var(--text-secondary, #64748b)' }} /></div>
                            <span className={styles.indLabel}>DIAS RESTANTES</span>
                        </div>
                        <div className={styles.indValueAccent}>{diasRestantes} {diasRestantes === 1 ? 'dia' : 'dias'}</div>
                    </div>
                    <div className={styles.indicatorRow}>
                        <div className={styles.indHeader}>
                            <div className={styles.indIcon}><TrendingUpIcon sx={{ fontSize: 18, color: 'var(--text-secondary, #64748b)' }} /></div>
                            <span className={styles.indLabel}>MÉDIA DIÁRIA</span>
                            <Tooltip title={
                                <span style={{ fontSize: 12, lineHeight: 1.4 }}>
                                    Isso é o quanto você precisa vender por dia útil (seg-sex) para bater a meta no fim do período. <br />
                                    <strong>Fórmula:</strong> (Meta - Vendido) ÷ Dias úteis restantes
                                </span>
                            } arrow placement="top" componentsProps={{ tooltip: { sx: { fontSize: 12 } } }}>
                                <span className={styles.helpIcon} style={{ cursor: 'pointer' }}>?</span>
                            </Tooltip>
                        </div>
                        <div className={styles.indValueChart}>{mediaDiaria > 0 ? formatCurrency(mediaDiaria) : 'R$ 0'}</div>
                    </div>
                    <div className={styles.indicatorRow}>
                        <div className={styles.indHeader}>
                            <div className={styles.indIcon}><OutlinedFlagIcon sx={{ fontSize: 18, color: 'var(--text-secondary, #64748b)' }} /></div>
                            <span className={styles.indLabel}>META PRÓXIMO MÊS</span>
                        </div>
                        <div className={styles.indValueChart}>{formatCurrency(nextMonthTarget && nextMonthTarget > 0 ? nextMonthTarget : targetSales)}</div>
                    </div>
                </div>
            </div>

            {/* Faixa horizontal inferior */}
            <div className={styles.bottomStrip}>
                {/* Progresso da Meta */}
                <div className={styles.stripItem}>
                    <span className={styles.stripTitle}>PROGRESSO DA META</span>
                    <span className={styles.stripValue}>{formatCurrency(currentSales)} de {formatCurrency(targetSales)}</span>
                    <div className={styles.progressBar}>
                        <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
                        <span className={styles.mark0}>0%</span>
                        <span className={styles.mark50}>50%</span>
                        <span className={styles.mark100}>100%</span>
                    </div>
                </div>



                {targetMargin > 0 && (
                    <div className={styles.stripItem}>
                        <span className={styles.stripTitle}>PROGRESSO DA MARGEM</span>
                        <span className={styles.stripValue}>{formatCurrency(currentMargin)} de {formatCurrency(targetMargin)}</span>
                        <div className={styles.progressBar}>
                            <div className={styles.progressFill} style={{ width: `${marginProgressPct}%`, background: 'var(--theme-accent, #1e293b)' }} />
                            <span className={styles.mark0}>0%</span>
                            <span className={styles.mark50}>50%</span>
                            <span className={styles.mark100}>100%</span>
                        </div>
                    </div>
                )}
                {targetMargin === 0 && nextMonthSales != null && nextMonthSales > 0 && nextMonthTarget != null && nextMonthTarget > 0 && (
                    <div className={styles.stripItem}>
                        <span className={styles.stripTitle}>META PRÓXIMO MÊS ({nextMonthScore?.toFixed(2) ?? 0}%)</span>
                        <span className={styles.stripValue}>{formatCurrency(nextMonthSales ?? 0)} vendido de {formatCurrency(nextMonthTarget)}</span>
                        <div className={styles.progressBar}>
                            <div className={styles.progressFill} style={{ width: `${Math.min((nextMonthSales ?? 0) * 100 / nextMonthTarget, 100)}%`, background: '#1B2A49' }} />
                            <span className={styles.mark0}>0%</span>
                            <span className={styles.mark50}>50%</span>
                            <span className={styles.mark100}>100%</span>
                        </div>
                    </div>
                )}


            </div>

            {simOpen && (() => {
                const simNum = Number(simValor.replace(/\./g, '').replace(',', '.')) || 0;
                const simMargemNum = Number(simMargem.replace(/\./g, '').replace(',', '.')) || 0;
                const newSales = currentSales + simNum;
                const newMargin = currentMargin + simMargemNum;
                // Recalcula o score completo com os valores informados (venda + margem manual)
                const hasSim = simNum > 0 || simMargemNum > 0;
                const totalSimMargem = simMargemNum > 0;

                // Calcula score BASE com os valores atuais usando a mesma fórmula do gauge
                // (para derivar corretamente o delta mesmo quando o backend usa scoreFinal com fatores extras)
                let scoreBase: number;
                if (targetSales === 0) {
                    scoreBase = 0;
                } else if (targetMargin > 0) {
                    const avBase = Math.min(currentSales / targetSales, 2);
                    const amBase = Math.min(currentMargin / targetMargin, 2);
                    scoreBase = (avBase * 0.5 + amBase * 0.5) * 100;
                } else {
                    scoreBase = Math.min((currentSales / targetSales) * 100, 200);
                }

                // Calcula score com valores simulados usando a mesma fórmula
                let scoreComSim: number;
                if (targetSales === 0) {
                    scoreComSim = 0;
                } else if (targetMargin > 0) {
                    const avSim = Math.min(newSales / targetSales, 2);
                    const amSim = Math.min(newMargin / targetMargin, 2);
                    scoreComSim = Math.min((avSim * 0.5 + amSim * 0.5) * 100, 200);
                } else {
                    scoreComSim = Math.min((newSales / targetSales) * 100, 200);
                }

                // Aplica o delta sobre o score real exibido (que pode ser scoreFinal do backend)
                // Isso garante que o ponto de partida do simulador seja sempre o score do gauge
                const delta = scoreComSim - scoreBase;
                const scoreSimulado = hasSim ? Math.min(score + delta, 200) : score;

                return (
                    <div className={styles.simOverlay} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div className={styles.simContent} style={{ background: 'var(--bg-widget)', borderRadius: 16, padding: '32px 48px', width: 560, maxWidth: '90%', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>Simular Meta</h3>
                                <button onClick={() => { setSimOpen(false); setSimValor(''); setSimMargem(''); }}
                                    style={{ background: 'var(--bg-page-dashboard)', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-secondary)', width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    ×
                                </button>
                            </div>
                            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'center' }}>
                                <div style={{ width: '100%', height: 180, position: 'relative' }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart margin={{ right: 50, left: 50 }}>
                                            <Pie data={(() => {
                                                const pct = Math.min(score, 100);
                                                const simPct = hasSim ? Math.min(scoreSimulado, 100) : Math.min(score, 100);
                                                const ganho = Math.max(simPct - pct, 0);
                                                const rest = Math.max(100 - simPct, 0.1);
                                                const vendasRest = Math.max(0, targetSales - (hasSim ? newSales : currentSales));
                                                const margemRest = targetMargin > 0 ? Math.max(0, targetMargin - (hasSim ? newMargin : currentMargin)) : 0;
                                                return [
                                                    { v: Math.max(pct, 0.1), name: 'atingido', totalVendido: hasSim ? newSales : currentSales, margem: totalSimMargem ? newMargin : currentMargin },
                                                    { v: Math.max(ganho, 0.01) },
                                                    { v: rest, name: 'restante', vendasRest, margemRest, restPct: rest }
                                                ];
                                            })()} cx="50%" cy="100%" startAngle={180} endAngle={0} innerRadius={90} outerRadius={120} dataKey="v" stroke="none" isAnimationActive={false}
                                                label={(props: any) => {
                                                    const { cx, cy, midAngle, outerRadius, name, payload } = props;
                                                    if (name !== 'atingido' && name !== 'restante') return null;
                                                    const angle = name === 'atingido' ? 140 : 40;
                                                    const RADIAN = Math.PI / 180;
                                                    const sin = Math.sin(-RADIAN * angle);
                                                    const cos = Math.cos(-RADIAN * angle);
                                                    const sx = cx + (outerRadius + 4) * cos;
                                                    const sy = cy + (outerRadius + 4) * sin;
                                                    const isAtingido = name === 'atingido';
                                                    const offRad = outerRadius + 24;
                                                    const mx = cx + offRad * cos;
                                                    const my = cy + offRad * sin;
                                                    const ex = isAtingido ? mx - 8 : mx + 8;
                                                    const ey = my;
                                                    const ta = isAtingido ? 'end' : 'start';
                                                    const dx = isAtingido ? ex - 4 : ex + 4;
                                                    return (
                                                        <g>
                                                            <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke="var(--text-tertiary)" fill="none" strokeWidth={1.5} />
                                                            <circle cx={ex} cy={ey} r={2} fill="var(--text-tertiary)" stroke="none" />
                                                            {name === 'atingido' && (
                                                                <>
                                                                    <text x={dx} y={ey - (empresa === 'IMBISEG' ? 8 : 20)} textAnchor={ta} fill="var(--text-secondary)" fontSize={11} fontWeight={700} style={{ textTransform: 'uppercase' }}>
                                                                        TOTAL VENDIDO
                                                                    </text>
                                                                    <text x={dx} y={ey + (empresa === 'IMBISEG' ? 8 : 4)} textAnchor={ta} fill="var(--text-primary)" fontSize={14} fontWeight={800}>
                                                                        {formatCurrencyFull(payload.totalVendido)}
                                                                    </text>
                                                                    {empresa !== 'IMBISEG' && (
                                                                        <>
                                                                            <text x={dx} y={ey + 26} textAnchor={ta} fill="var(--text-secondary)" fontSize={11} fontWeight={700} style={{ textTransform: 'uppercase' }}>
                                                                                MARGEM
                                                                            </text>
                                                                            <text x={dx} y={ey + 40} textAnchor={ta} fill="var(--text-primary)" fontSize={14} fontWeight={800}>
                                                                                {formatCurrencyFull(payload.margem)}
                                                                            </text>
                                                                        </>
                                                                    )}
                                                                </>
                                                            )}
                                                            {name === 'restante' && (
                                                                <>
                                                                    <text x={dx} y={ey - (empresa === 'IMBISEG' ? 8 : 20)} textAnchor={ta} fill="var(--text-secondary)" fontSize={11} fontWeight={700} style={{ textTransform: 'uppercase' }}>
                                                                        VENDA RESTANTE
                                                                    </text>
                                                                    <text x={dx} y={ey + (empresa === 'IMBISEG' ? 8 : 4)} textAnchor={ta} fill="var(--text-primary)" fontSize={14} fontWeight={800}>
                                                                        {formatCurrencyFull(payload.vendasRest)}
                                                                    </text>
                                                                    {empresa !== 'IMBISEG' && (
                                                                        <>
                                                                            <text x={dx} y={ey + 26} textAnchor={ta} fill="var(--text-secondary)" fontSize={11} fontWeight={700} style={{ textTransform: 'uppercase' }}>
                                                                                MARGEM RESTANTE
                                                                            </text>
                                                                            <text x={dx} y={ey + 40} textAnchor={ta} fill="var(--text-primary)" fontSize={14} fontWeight={800}>
                                                                                {formatCurrencyFull(payload.margemRest)}
                                                                            </text>
                                                                        </>
                                                                    )}
                                                                </>
                                                            )}
                                                        </g>
                                                    );
                                                }}
                                                labelLine={false}
                                            >
                                                <Cell fill={primary} />
                                                <Cell fill={simNum > 0 ? '#16a34a' : 'transparent'} />
                                                <Cell fill="var(--gauge-track)" />
                                            </Pie>
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div style={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', lineHeight: 1, textAlign: 'center' }}>
                                        <span key={`score-${simNum}-${simMargemNum}`} className={`${styles.simScore} ${simNum > 0 ? styles.simScoreChanged : ''}`} style={{ fontSize: 32, fontWeight: 800, color: 'var(--text-primary)' }}>{simNum > 0 ? (scoreSimulado >= 100 ? scoreSimulado.toFixed(1) : scoreSimulado.toFixed(2)) : (score >= 100 ? score.toFixed(1) : score.toFixed(2))}%</span>
                                        {simNum > 0 && <span key={`gain-${simNum}-${simMargemNum}`} className={styles.simGainBadge} style={{ fontSize: 14, fontWeight: 700, color: '#16a34a', display: 'block', marginTop: 4 }}>+{(scoreSimulado - score).toFixed(1)}% simulado</span>}
                                    </div>
                                </div>

                            </div>

                            <label style={{ fontSize: 14, fontWeight: 700, display: 'block', marginBottom: 8, color: 'var(--text-primary)' }}>Valor da venda (R$)</label>
                            <input value={simValor} onChange={e => {
                                const input = e.target;
                                const pos = input.selectionStart || 0;
                                let val = input.value.replace(/[^0-9,]/g, '');
                                const firstComma = val.indexOf(',');
                                if (firstComma !== -1) {
                                    val = val.substring(0, firstComma + 1) + val.substring(firstComma + 1).replace(/,/g, '');
                                }
                                const parts = val.split(',');
                                const decPart = parts.length > 1 ? ',' + parts[1].slice(0, 2) : '';
                                const formatted = parts[0] + decPart;
                                setSimValor(formatted);
                                requestAnimationFrame(() => {
                                    input.setSelectionRange(pos, pos);
                                });
                            }}
                                placeholder="0"
                                maxLength={15}
                                style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '2px solid var(--border-primary)', background: 'transparent', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }}
                            />
                            {empresa !== 'IMBISEG' && <label style={{ fontSize: 14, fontWeight: 700, display: 'block', marginBottom: 8, marginTop: 16, color: 'var(--text-primary)' }}>Margem da venda (R$)</label>}
                            {empresa !== 'IMBISEG' && <input value={simMargem} onChange={e => {
                                const input = e.target;
                                const pos = input.selectionStart || 0;
                                let val = input.value.replace(/[^0-9,]/g, '');
                                const firstComma = val.indexOf(',');
                                if (firstComma !== -1) {
                                    val = val.substring(0, firstComma + 1) + val.substring(firstComma + 1).replace(/,/g, '');
                                }
                                const parts = val.split(',');
                                const decPart = parts.length > 1 ? ',' + parts[1].slice(0, 2) : '';
                                const formatted = parts[0] + decPart;
                                setSimMargem(formatted);
                                requestAnimationFrame(() => {
                                    input.setSelectionRange(pos, pos);
                                });
                            }}
                                placeholder="0"
                                maxLength={15}
                                style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '2px solid var(--border-primary)', background: 'transparent', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }}
                            />}

                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default ModernGoalWidget;